import React, { useCallback, useMemo, useState } from 'react';
import { OptionsPageShell } from '../../components/options/OptionsPageShell';
import OptionsLoadingState from '../../components/options/OptionsLoadingState';
import { OptionsOnboardingCard } from '../../components/options/OptionsOnboardingCard';
import { OptionsOpenPositionsFilters } from '../../components/options/OptionsOpenPositionsFilters';
import { OptionsOpenPositionCard } from '../../components/options/OptionsOpenPositionCard';
import { OptionsPositionAlertQuickDialog } from '../../components/options/OptionsPositionAlertQuickDialog';
import { OptionsRiskSummaryCompact } from '../../components/options/OptionsRiskSummaryCompact';
import { OptionsManagementPositionCard } from '../../components/options/OptionsManagementPositionCard';
import { useOptionsPortfolioRiskData } from '../../hooks/useOptionsPortfolioRiskData';
import { useOptionsOverviewState } from '../../hooks/useOptionsOverviewState';
import { useOptionsOpenPositionsData } from '../../hooks/useOptionsOpenPositionsData';
import { useOptionsMode } from '../../context/OptionsModeContext';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import useOptionsAlertsData from '../../hooks/useOptionsAlertsData';

export default function OptionsPositions() {
  const { isDemoMode } = useOptionsMode();
  const { t } = useOptionsLocale();
  const [selectedAlertOperation, setSelectedAlertOperation] = useState(null);
  const { connected, loading: overviewLoading, reloadOverview, overview } = useOptionsOverviewState();
  const { accountData, loading: portfolioRiskLoading, reloadAccountData } = useOptionsPortfolioRiskData(connected);
  const { data: alertsData, createRule, updateRule, toggleRule } = useOptionsAlertsData();
  const reloadPositionsContext = useCallback(
    async () => Promise.all([reloadOverview?.(), reloadAccountData?.()]),
    [reloadOverview, reloadAccountData]
  );
  const {
    loading,
    syncing,
    items,
    filteredItems,
    selectedCurrency,
    setSelectedCurrency,
    underlyingFilter,
    setUnderlyingFilter,
    strategyFilter,
    setStrategyFilter,
    strategyOptions,
    sortBy,
    setSortBy,
    handleSync,
  } = useOptionsOpenPositionsData(connected, reloadPositionsContext);

  const hasFilters = selectedCurrency !== 'usd' || underlyingFilter !== 'all' || strategyFilter !== 'all';
  const hasOpenPositions = items.length > 0;
  const operationAlertCountMap = useMemo(() => {
    const map = {};
    [...(alertsData?.active_rules || []), ...(alertsData?.paused_rules || [])].forEach((rule) => {
      if (!rule.operation_id) return;
      map[rule.operation_id] = (map[rule.operation_id] || 0) + (rule.enabled ? 1 : 0);
    });
    return map;
  }, [alertsData]);
  const managementPositions = useMemo(() => {
    if (strategyFilter !== 'all') return [];
    const items = accountData?.management_positions || [];
    const effectiveUnderlying = String(selectedCurrency === 'usd' ? underlyingFilter : selectedCurrency).toUpperCase();
    return items.filter((item) => effectiveUnderlying === 'ALL' || String(item?.underlying || item?.currency || '').toUpperCase() === effectiveUnderlying);
  }, [accountData, selectedCurrency, underlyingFilter, strategyFilter]);
  const totalManagementPositionsCount = accountData?.management_positions?.length || 0;
  const hasAnyOpenPositions = hasOpenPositions || managementPositions.length > 0;

  return (
    <OptionsPageShell
      title={t('positions.title')}
      description={t('positions.description')}
      eyebrow={t('positions.eyebrow')}
      hideHeader
    >
      {overviewLoading ? (
        <OptionsLoadingState
          title={t('common.connectDeribit')}
          description={t('positions.description')}
          testId="options-open-positions-loading-overview"
        />
      ) : !connected ? (
        <OptionsOnboardingCard
          title={t('onboarding.title')}
          description={t('onboarding.description')}
          connectLabel={t('common.connectDeribit')}
          securityTitle={t('onboarding.securityTitle')}
          securityBody={t('onboarding.securityBody')}
          securityDetails={t('onboarding.securityDetails')}
        />
      ) : loading ? (
        <OptionsLoadingState
          title={t('common.syncingNow')}
          description={t('positions.description')}
          testId="options-open-positions-loading-state"
        />
      ) : (
        <div className="space-y-6" data-testid="options-open-positions-root">
          <OptionsOpenPositionsFilters
            selectedCurrency={selectedCurrency}
            onCurrencyChange={setSelectedCurrency}
            underlyingFilter={underlyingFilter}
            onUnderlyingChange={setUnderlyingFilter}
            strategyFilter={strategyFilter}
            onStrategyChange={setStrategyFilter}
            strategyOptions={strategyOptions}
            sortBy={sortBy}
            onSortChange={setSortBy}
            syncing={syncing}
            onSync={handleSync}
            totalCount={items.length + totalManagementPositionsCount}
            filteredCount={filteredItems.length + managementPositions.length}
            showSyncButton={!isDemoMode && overview?.can_sync !== false}
          />

          <OptionsRiskSummaryCompact
            accountData={accountData}
            loading={portfolioRiskLoading}
            openItems={items}
            selectedCurrency={selectedCurrency}
            underlyingFilter={underlyingFilter}
          />

          {!hasAnyOpenPositions ? (
            <div className="rounded-[32px] border border-dashed border-white/10 bg-white/[0.03] px-6 py-12 text-center" data-testid="options-open-positions-empty-state">
              <p className="text-2xl font-black font-heading text-white" data-testid="options-open-positions-empty-title">{t('positions.emptyTitle')}</p>
              <p className="mx-auto mt-3 max-w-2xl text-sm text-white/60 sm:text-base" data-testid="options-open-positions-empty-description">
                {t('positions.emptyDescription')}
              </p>
            </div>
          ) : !filteredItems.length && !managementPositions.length ? (
            <div className="rounded-[32px] border border-dashed border-white/10 bg-white/[0.03] px-6 py-12 text-center" data-testid="options-open-positions-filter-empty-state">
              <p className="text-2xl font-black font-heading text-white" data-testid="options-open-positions-filter-empty-title">{t('positions.filterEmptyTitle')}</p>
              <p className="mx-auto mt-3 max-w-2xl text-sm text-white/60 sm:text-base" data-testid="options-open-positions-filter-empty-description">
                {hasFilters ? t('positions.filterEmptyWithFilters') : t('positions.filterEmptyWithoutFilters')}
              </p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="options-open-positions-list">
              {filteredItems.map((item, index) => (
                <OptionsOpenPositionCard key={item.id} item={item} index={index} selectedCurrency={selectedCurrency} alertCount={operationAlertCountMap[item.id] || 0} onAlertClick={setSelectedAlertOperation} />
              ))}
            </div>
          )}

          {managementPositions.length ? (
            <div className="space-y-4" data-testid="options-management-positions-section">
              <div className="space-y-1">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/45" data-testid="options-management-positions-title">{t('positions.managementPositionsTitle')}</p>
                <p className="text-sm text-white/55" data-testid="options-management-positions-description">{t('positions.managementPositionsDescription')}</p>
              </div>
              {managementPositions.map((item, index) => (
                <OptionsManagementPositionCard
                  key={item.id}
                  item={item}
                  index={index}
                />
              ))}
            </div>
          ) : null}

          <OptionsPositionAlertQuickDialog
            open={!!selectedAlertOperation}
            onOpenChange={(open) => { if (!open) setSelectedAlertOperation(null); }}
            operation={selectedAlertOperation}
            rules={[...(alertsData?.active_rules || []), ...(alertsData?.paused_rules || [])]}
            preferences={alertsData?.preferences}
            onCreateRule={createRule}
            onUpdateRule={updateRule}
            onToggleRule={toggleRule}
          />
        </div>
      )}
    </OptionsPageShell>
  );
}